// src/components/portal/archivesandfolders.jsx
import React, { useState, useEffect, useCallback } from "react";
import "./portal-components.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

import IconAdd from "./res/Icon-Add.svg";
import IconEdit from "./res/Icon-Edit.svg";
import IconTrash from "./res/Icon-Trash.svg";
import IconCloud from "./res/Icon-Cloud.svg";
import IconFolder from "./res/Icon-Folder.svg";

const ArchivesAndFolders = () => {
  const { user } = useAuth();
  
  const [profiles, setProfiles] = useState([]);
  const [storage, setStorage] = useState(null);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    profile_name: "",
    storage_type: "cloud",
    local_folder_path: "",
    retention_days: 30,
    max_versions: 10,
    compress_backups: true,
    description: ""
  });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch profiles
  const fetchProfiles = useCallback(async () => {
    if (!user?.username) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/archives/profiles?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setProfiles(data.data?.profiles || []);
      }
    } catch (err) {
      console.error("Failed to fetch profiles:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  // Fetch storage usage
  const fetchStorage = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/archives/storage?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setStorage(data.data?.storage || null);
      }
    } catch (err) {
      console.error("Failed to fetch storage:", err);
    }
  }, [user?.username]);

  // Fetch recent archives
  const fetchArchives = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/archives?username=${encodeURIComponent(user.username)}&limit=10`);
      const data = await res.json();
      if (data.status === "success") {
        setArchives(data.data?.archives || []);
      }
    } catch (err) {
      console.error("Failed to fetch archives:", err);
    }
  }, [user?.username]);

  useEffect(() => {
    fetchProfiles();
    fetchStorage();
    fetchArchives();
  }, [fetchProfiles, fetchStorage, fetchArchives]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!formData.profile_name.trim()) {
      setFormError("Profile name is required");
      return;
    }
    if (formData.storage_type === 'local' && !formData.local_folder_path.trim()) {
      setFormError("Local folder path is required for local storage");
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = editingProfile 
        ? `${API_BASE_URL}/api/archives/profiles/${editingProfile.id}`
        : `${API_BASE_URL}/api/archives/profiles`;
      
      const res = await fetch(endpoint, {
        method: editingProfile ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username, ...formData })
      });
      
      const data = await res.json();
      if (data.status === "success") {
        setShowForm(false);
        setEditingProfile(null);
        resetForm();
        fetchProfiles();
      } else {
        setFormError(data.reason || "Failed to save profile");
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      profile_name: "",
      storage_type: "cloud",
      local_folder_path: "",
      retention_days: 30,
      max_versions: 10,
      compress_backups: true,
      description: ""
    });
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setFormData({
      profile_name: profile.profile_name,
      storage_type: profile.storage_type,
      local_folder_path: profile.local_folder_path || "",
      retention_days: profile.retention_days,
      max_versions: profile.max_versions,
      compress_backups: profile.compress_backups,
      description: profile.description || ""
    });
    setShowForm(true);
    setFormError("");
  };

  const handleDeleteProfile = async (profile) => {
    if (!window.confirm(`Delete profile "${profile.profile_name}"?`)) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/archives/profiles/${profile.id}?username=${encodeURIComponent(user.username)}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchProfiles();
      } else {
        alert(data.detail || data.reason || "Failed to delete profile");
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const getStorageBarClass = () => {
    if (!storage) return '';
    if (storage.usage_percent >= 90) return 'danger';
    if (storage.usage_percent >= 70) return 'warning';
    return '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <>
      <div className="right-top">
        <div className="right-top-title">Archives & Folders</div>
      </div>

      <div className="right-middle archive-middle">
        {/* CLOUD STORAGE SECTION */}
        <div className="service-section" style={{ flex: '0 0 auto' }}>
          <div className="cell service-cell">
            <div className="cell-header">
              <span>☁️ Cloud Storage</span>
            </div>
            <div className="cell-body">
              {storage ? (
                <div className="storage-overview">
                  <div className="storage-info">
                    <div className="storage-main">
                      <span className="storage-used">{storage.used_formatted}</span>
                      <span className="storage-separator"> of </span>
                      <span className="storage-limit">{storage.limit_formatted}</span>
                    </div>
                    <div className="storage-available">
                      {storage.available_formatted} available
                    </div>
                  </div>
                  <div className="storage-bar-container">
                    <div className="storage-bar">
                      <div 
                        className={`storage-bar-fill ${getStorageBarClass()}`}
                        style={{ width: `${Math.min(storage.usage_percent, 100)}%` }}
                      />
                    </div>
                    <div className="storage-text">
                      <span>{storage.usage_percent}% used</span>
                      <span>5GB limit per user</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="loading">Loading storage info...</div>
              )}
            </div>
          </div>
        </div>

        {/* ARCHIVE PROFILES SECTION */}
        <div className="service-section" style={{ flex: 1 }}>
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Archive Profiles</span>
              <div className="header-actions">
                <button className="icon-btn add-btn" onClick={() => { setShowForm(true); setEditingProfile(null); resetForm(); setFormError(""); }}>
                  <img src={IconAdd} alt="Add" />
                  <span>Add Profile</span>
                </button>
              </div>
            </div>
            <div className="cell-body">
              {loading ? (
                <div className="loading">Loading profiles...</div>
              ) : profiles.length === 0 ? (
                <div className="empty-state">
                  <img src={IconFolder} alt="No profiles" className="empty-icon" />
                  <p>No archive profiles created</p>
                  <p className="empty-hint">Create profiles to define where backups are stored</p>
                </div>
              ) : (
                <div className="profiles-grid">
                  {profiles.map(profile => (
                    <div key={profile.id} className="profile-card">
                      <div className="profile-header">
                        <div>
                          <div className="profile-name">
                            {profile.storage_type === 'cloud' ? '☁️' : '📁'} {profile.profile_name}
                          </div>
                          <div className="profile-meta">
                            {profile.storage_type === 'cloud' ? 'Cloud Storage' : 'Local Storage'}
                            {profile.archive_count > 0 && ` • ${profile.archive_count} archives`}
                            {profile.total_size_bytes > 0 && ` • ${profile.total_size_formatted}`}
                          </div>
                        </div>
                        <div className="profile-actions">
                          <button className="icon-btn-sm edit" onClick={() => handleEditProfile(profile)}>
                            <img src={IconEdit} alt="Edit" />
                          </button>
                          <button className="icon-btn-sm delete" onClick={() => handleDeleteProfile(profile)}>
                            <img src={IconTrash} alt="Delete" />
                          </button>
                        </div>
                      </div>
                      <div className="profile-details">
                        {profile.storage_type === 'local' && profile.local_folder_path && (
                          <div className="profile-detail">
                            <span className="detail-label">Path:</span>
                            <code>{profile.local_folder_path}</code>
                          </div>
                        )}
                        <div className="profile-detail">
                          <span className="detail-label">Retention:</span>
                          <span>{profile.retention_days} days</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Max Versions:</span>
                          <span>{profile.max_versions}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Compression:</span>
                          <span>{profile.compress_backups ? 'Enabled' : 'Disabled'}</span>
                        </div>
                      </div>
                      {profile.description && (
                        <div className="profile-description">{profile.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RECENT ARCHIVES SECTION */}
        <div className="service-section" style={{ flex: 1 }}>
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Recent Archives</span>
            </div>
            <div className="cell-body">
              {archives.length === 0 ? (
                <div className="empty-state">
                  <p>No archives yet</p>
                  <p className="empty-hint">Archives will appear here after backup jobs run</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Device</th>
                        <th>Size</th>
                        <th>Storage</th>
                        <th>Created</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archives.map(archive => (
                        <tr key={archive.id}>
                          <td><code>{archive.file_name}</code></td>
                          <td>{archive.device_hostname || '-'}</td>
                          <td>{archive.file_size_formatted}</td>
                          <td>{archive.storage_type === 'cloud' ? '☁️ Cloud' : '📁 Local'}</td>
                          <td className="date-cell">{formatDate(archive.created_at)}</td>
                          <td>
                            <span className={`status-badge status-${archive.status}`}>
                              <span className="status-dot"></span>
                              {archive.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PROFILE FORM MODAL */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProfile ? "Edit Archive Profile" : "Create Archive Profile"}</h3>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSaveProfile}>
              <div className="form-row">
                <label>Profile Name *</label>
                <input 
                  type="text" 
                  value={formData.profile_name} 
                  onChange={e => setFormData({...formData, profile_name: e.target.value})} 
                  placeholder="e.g., Daily Backups" 
                />
              </div>
              
              <div className="form-row">
                <label>Storage Location *</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input 
                      type="radio" 
                      name="storage_type" 
                      value="cloud"
                      checked={formData.storage_type === 'cloud'}
                      onChange={e => setFormData({...formData, storage_type: e.target.value})}
                    />
                    <span className="radio-label">
                      <img src={IconCloud} alt="Cloud" style={{ width: 16, height: 16, marginRight: 6 }} />
                      Cloud Storage (5GB limit)
                    </span>
                  </label>
                  <label className="radio-option">
                    <input 
                      type="radio" 
                      name="storage_type" 
                      value="local"
                      checked={formData.storage_type === 'local'}
                      onChange={e => setFormData({...formData, storage_type: e.target.value})}
                    />
                    <span className="radio-label">
                      <img src={IconFolder} alt="Local" style={{ width: 16, height: 16, marginRight: 6 }} />
                      Local Host (Agent folder)
                    </span>
                  </label>
                </div>
              </div>

              {formData.storage_type === 'local' && (
                <div className="form-row">
                  <label>Local Folder Path *</label>
                  <input 
                    type="text" 
                    value={formData.local_folder_path} 
                    onChange={e => setFormData({...formData, local_folder_path: e.target.value})} 
                    placeholder="e.g., C:\Backups\NetworkConfigs" 
                  />
                  <small className="form-hint">This path must exist on the agent machine</small>
                </div>
              )}

              <div className="form-row-half">
                <div className="form-row">
                  <label>Retention (days)</label>
                  <input 
                    type="number" 
                    value={formData.retention_days} 
                    onChange={e => setFormData({...formData, retention_days: parseInt(e.target.value) || 30})}
                    min="1"
                    max="365"
                  />
                </div>
                <div className="form-row">
                  <label>Max Versions</label>
                  <input 
                    type="number" 
                    value={formData.max_versions} 
                    onChange={e => setFormData({...formData, max_versions: parseInt(e.target.value) || 10})}
                    min="1"
                    max="100"
                  />
                </div>
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={formData.compress_backups}
                    onChange={e => setFormData({...formData, compress_backups: e.target.checked})}
                  />
                  <span>Compress backup files (recommended)</span>
                </label>
              </div>

              <div className="form-row">
                <label>Description (Optional)</label>
                <textarea 
                  value={formData.description} 
                  onChange={e => setFormData({...formData, description: e.target.value})} 
                  placeholder="Enter description..." 
                  rows="2" 
                />
              </div>

              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="right-bottom-bar"></div>
    </>
  );
};

export default ArchivesAndFolders;
